// src/pages/settings/AssignRoles.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  Checkbox,
  SimpleGrid,
  useDisclosure,
  Flex,
  Grid,
} from '@chakra-ui/react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db, functions } from '../../firebaseConfig';
import useOrgClaims from '../../hooks/useOrgClaims';

// keep + and digits only for storage; Auth phone verification can be added later
const normPhone = (v) => (v || '').replace(/[^\+\d]/g, '');

export default function AssignRoles() {
  const toast = useToast();
  const { orgId, role: myRole } = useOrgClaims();
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);

  // Modals / forms
  const addModal = useDisclosure(); // <-- useDisclosure for Add User
  const [showEdit, setShowEdit] = useState(null); // { uid, firstName, lastName, email, phone }
  const [tempPassword, setTempPassword] = useState(null);

  const [addForm, setAddForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Primary role checkboxes (mutually exclusive for now)
  const [roleChecks, setRoleChecks] = useState({
    admin: true,
    superadmin: false,
    // Future roles (shown but disabled for now)
    leadGenerator: false,
    leadVerifier: false,
    branchMgr: false,
    salesMgr: false,
    surveyor: false,
    fitter: false,
    finance: false,
  });

  const isSuperadmin = myRole === 'superadmin';
  const call = (name) => httpsCallable(functions, name);

  // Load members from the Org (single source of truth)
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    const q = query(collection(db, 'organizations', orgId, 'members'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setMembers(rows);
        setLoading(false);
      },
      (err) => {
        console.error('[AssignRoles] members load error:', err);
        toast({
          title: 'Error loading members',
          description: String(err.message || err),
          status: 'error',
        });
        setLoading(false);
      }
    );
    return () => unsub();
  }, [orgId, toast]);

  const superadminCount = useMemo(
    () => members.filter((m) => m.role === 'superadmin').length,
    [members]
  );

  // --- Actions (functions payloads match your backend) ---
  const handleAdd = async () => {
    if (!orgId) return;
    const { firstName, lastName, email, phone } = addForm;
    if (!firstName || !lastName || !email) {
      toast({ title: 'Please fill First name, Last name and Email', status: 'warning' });
      return;
    }
    const role = roleChecks.superadmin ? 'superadmin' : 'admin';
    try {
      const res = await call('createUserForOrg')({
        firstName,
        lastName,
        email,
        role,
        phone: normPhone(phone),
      });
      const pwd = res?.data?.password; // backend returns { uid, password }
      setTempPassword(pwd || null);
      toast({ title: 'User created', status: 'success' });

      // reset + close
      addModal.onClose();
      setAddForm({ firstName: '', lastName: '', email: '', phone: '' });
      setRoleChecks({
        admin: true,
        superadmin: false,
        leadGenerator: false,
        leadVerifier: false,
        branchMgr: false,
        salesMgr: false,
        surveyor: false,
        fitter: false,
        finance: false,
      });
    } catch (e) {
      toast({ title: 'Create failed', description: String(e.message || e), status: 'error' });
    }
  };

  const handleEditSave = async () => {
    if (!orgId || !showEdit?.uid) return;
    try {
      await call('updateMemberProfileForOrg')({
        targetUid: showEdit.uid,
        firstName: showEdit.firstName,
        lastName: showEdit.lastName,
        email: showEdit.email,
        phone: normPhone(showEdit.phone),
      });
      toast({
        title: 'Saved',
        description: 'If you changed email, the user may need to sign out/in.',
        status: 'success',
      });
      setShowEdit(null);
    } catch (e) {
      toast({ title: 'Save failed', description: String(e.message || e), status: 'error' });
    }
  };

  const handlePromoteDemote = async (m) => {
    const newRole = m.role === 'superadmin' ? 'admin' : 'superadmin';
    if (m.role === 'superadmin' && superadminCount <= 1) {
      toast({
        title: 'Blocked',
        description: "You can't demote the last super admin.",
        status: 'warning',
      });
      return;
    }
    if (newRole === 'superadmin' && superadminCount >= 3) {
      toast({
        title: 'Limit reached',
        description: 'Max 3 super admins per org.',
        status: 'warning',
      });
      return;
    }
    try {
      await call('setUserRoleForOrg')({ targetUid: m.id, newRole });
      toast({ title: `Role changed to ${newRole}`, status: 'success' });
    } catch (e) {
      toast({ title: 'Role change failed', description: String(e.message || e), status: 'error' });
    }
  };

  const handleSuspendRestore = async (m) => {
    if (m.role === 'superadmin' && superadminCount <= 1 && m.status === 'active') {
      toast({
        title: 'Blocked',
        description: "You can't suspend the last super admin.",
        status: 'warning',
      });
      return;
    }
    try {
      await call('suspendMember')({ targetUid: m.id, suspend: m.status === 'active' });
      toast({ title: m.status === 'active' ? 'Suspended' : 'Restored', status: 'success' });
    } catch (e) {
      toast({ title: 'Action failed', description: String(e.message || e), status: 'error' });
    }
  };

  const handleRemove = async (m) => {
    if (m.role === 'superadmin' && superadminCount <= 1) {
      toast({
        title: 'Blocked',
        description: "You can't remove the last super admin.",
        status: 'warning',
      });
      return;
    }
    if (!window.confirm(`Remove ${m.firstName || ''} ${m.lastName || ''}?`)) return;
    try {
      await call('removeMemberFromOrg')({ targetUid: m.id });
      toast({ title: 'Removed', status: 'success' });
    } catch (e) {
      toast({ title: 'Remove failed', description: String(e.message || e), status: 'error' });
    }
  };

  const handleResetPassword = async (m) => {
    try {
      const res = await call('resetPasswordForOrgMember')({ targetUid: m.id });
      const pwd = res?.data?.password;
      setTempPassword(pwd || null);
      toast({ title: 'Password reset', status: 'success' });
    } catch (e) {
      toast({ title: 'Reset failed', description: String(e.message || e), status: 'error' });
    }
  };

  // Primary role toggle
  const togglePrimary = (which) => {
    if (which === 'admin') setRoleChecks((r) => ({ ...r, admin: true, superadmin: false }));
    else setRoleChecks((r) => ({ ...r, admin: false, superadmin: true }));
  };

  const roleLabel = (r) => (r === 'superadmin' ? 'Super Admin' : 'Admin');
  const myUid = auth.currentUser?.uid;

  return (
    <Box borderWidth={1} borderRadius="lg" p={6} mt={0} boxShadow="lg" bg="white">
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={1}>
            <Heading size="lg" color="blue.600">
              Assign Roles
            </Heading>
            <Text fontSize="md" color="gray.600">
              Manage user roles and permissions for your organisation.
            </Text>
          </VStack>
          <Button colorScheme="blue" size="lg" onClick={addModal.onOpen}>
            Add user
          </Button>
        </HStack>

        {loading ? (
          <HStack spacing={4}>
            <Spinner size="lg" />
            <Text fontSize="lg">Loading…</Text>
          </HStack>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>NAME</Th>
                  <Th>EMAIL</Th>
                  <Th>PHONE</Th>
                  <Th>ROLE</Th>
                  <Th>STATUS</Th>
                  <Th isNumeric>ACTIONS</Th>
                </Tr>
              </Thead>
              <Tbody>
                {members.map((m) => {
                  const isMe = m.id === myUid;
                  const canManage = isSuperadmin;
                  const canDemote = canManage && !(m.role === 'superadmin' && superadminCount <= 1);
                  const canSuspend =
                    canManage &&
                    !(m.role === 'superadmin' && superadminCount <= 1 && m.status === 'active');
                  const canRemove =
                    canManage && !(m.role === 'superadmin' && superadminCount <= 1) && !isMe;

                  return (
                    <Tr key={m.id}>
                      <Td>{(m.firstName || '') + ' ' + (m.lastName || '')}</Td>
                      <Td>{m.email}</Td>
                      <Td>{m.phone}</Td>
                      <Td>
                        <Heading
                          as="span"
                          size="sm"
                          color={m.role === 'superadmin' ? 'purple.600' : 'blue.600'}
                        >
                          {roleLabel(m.role)}
                        </Heading>
                      </Td>
                      <Td>
                        <Heading
                          as="span"
                          size="sm"
                          color={m.status === 'active' ? 'green.600' : 'red.600'}
                        >
                          {m.status || 'active'}
                        </Heading>
                      </Td>
                      <Td isNumeric>
                        <Flex justify="flex-end" spacing={2} flexWrap="wrap">
                          <Button
                            size="md"
                            variant="outline"
                            onClick={() => handlePromoteDemote(m)}
                            isDisabled={!canDemote}
                          >
                            {m.role === 'superadmin' ? 'Demote' : 'Promote'}
                          </Button>
                          <Button
                            size="md"
                            variant="outline"
                            onClick={() => setShowEdit({ uid: m.id, ...m })}
                            isDisabled={!canManage}
                          >
                            Edit
                          </Button>
                          <Button
                            size="md"
                            variant="outline"
                            onClick={() => handleResetPassword(m)}
                            isDisabled={!canManage}
                          >
                            Reset password
                          </Button>
                          <Button
                            size="md"
                            variant="outline"
                            onClick={() => handleSuspendRestore(m)}
                            isDisabled={!canSuspend}
                          >
                            {m.status === 'active' ? 'Suspend' : 'Restore'}
                          </Button>
                          <Button
                            size="md"
                            variant="outline"
                            colorScheme="red"
                            onClick={() => handleRemove(m)}
                            isDisabled={!canRemove}
                          >
                            Remove
                          </Button>
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
                {members.length === 0 && (
                  <Tr>
                    <Td colSpan={6}>
                      <Text color="gray.500">No members yet.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Add user modal */}
        <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} isCentered size="xl">
          <ModalOverlay />
          <ModalContent maxH="80vh">
            <ModalHeader>Add user</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto">
              <VStack spacing={4} align="stretch" maxW="100%">
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      First name
                    </FormLabel>
                    <Input
                      size="lg"
                      value={addForm.firstName}
                      onChange={(e) => setAddForm((v) => ({ ...v, firstName: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Last name
                    </FormLabel>
                    <Input
                      size="lg"
                      value={addForm.lastName}
                      onChange={(e) => setAddForm((v) => ({ ...v, lastName: e.target.value }))}
                    />
                  </FormControl>
                </Grid>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Email
                    </FormLabel>
                    <Input
                      size="lg"
                      type="email"
                      value={addForm.email}
                      onChange={(e) => setAddForm((v) => ({ ...v, email: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Mobile (with country code)
                    </FormLabel>
                    <Input
                      size="lg"
                      placeholder="+44XXXXXXXXXX"
                      value={addForm.phone}
                      onChange={(e) => setAddForm((v) => ({ ...v, phone: e.target.value }))}
                    />
                  </FormControl>
                </Grid>

                <Box>
                  <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                    Role(s)
                  </FormLabel>
                  {/* Primary roles */}
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={2}>
                    <Checkbox
                      isChecked={roleChecks.admin}
                      onChange={() => togglePrimary('admin')}
                      colorScheme="blue"
                    >
                      Admin
                    </Checkbox>
                    <Checkbox
                      isChecked={roleChecks.superadmin}
                      onChange={() => togglePrimary('superadmin')}
                      colorScheme="blue"
                    >
                      Super Admin
                    </Checkbox>
                  </SimpleGrid>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Choose one primary role for now. (Multi-role support is coming.)
                  </Text>

                  {/* Future roles (disabled for now) */}
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                    <Checkbox isDisabled colorScheme="blue">
                      Lead Generator
                    </Checkbox>
                    <Checkbox isDisabled colorScheme="blue">
                      Lead Verifier
                    </Checkbox>
                    <Checkbox isDisabled colorScheme="blue">
                      Branch Mgr
                    </Checkbox>
                    <Checkbox isDisabled colorScheme="blue">
                      Sales Mgr
                    </Checkbox>
                    <Checkbox isDisabled colorScheme="blue">
                      Surveyor
                    </Checkbox>
                    <Checkbox isDisabled colorScheme="blue">
                      Fitter
                    </Checkbox>
                    <Checkbox isDisabled colorScheme="blue">
                      Finance
                    </Checkbox>
                  </SimpleGrid>
                </Box>

                {tempPassword && (
                  <Alert status="success">
                    <AlertIcon />
                    Temp password: <b style={{ marginLeft: 6 }}>{tempPassword}</b>
                  </Alert>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button size="lg" variant="ghost" mr={3} onClick={addModal.onClose}>
                Cancel
              </Button>
              <Button size="lg" colorScheme="blue" onClick={handleAdd} isDisabled={!isSuperadmin}>
                Create
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Edit person */}
        <Modal isOpen={!!showEdit} onClose={() => setShowEdit(null)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit person</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {showEdit && (
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      First name
                    </FormLabel>
                    <Input
                      size="lg"
                      value={showEdit.firstName || ''}
                      onChange={(e) => setShowEdit((v) => ({ ...v, firstName: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Last name
                    </FormLabel>
                    <Input
                      size="lg"
                      value={showEdit.lastName || ''}
                      onChange={(e) => setShowEdit((v) => ({ ...v, lastName: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Email
                    </FormLabel>
                    <Input
                      size="lg"
                      type="email"
                      value={showEdit.email || ''}
                      onChange={(e) => setShowEdit((v) => ({ ...v, email: e.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                      Mobile (with country code)
                    </FormLabel>
                    <Input
                      size="lg"
                      placeholder="+44XXXXXXXXXX"
                      value={showEdit.phone || ''}
                      onChange={(e) => setShowEdit((v) => ({ ...v, phone: e.target.value }))}
                    />
                  </FormControl>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button size="lg" variant="ghost" mr={3} onClick={() => setShowEdit(null)}>
                Cancel
              </Button>
              <Button
                size="lg"
                colorScheme="blue"
                onClick={handleEditSave}
                isDisabled={!isSuperadmin}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Password reveal modal */}
        <Modal isOpen={!!tempPassword} onClose={() => setTempPassword(null)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Temporary password</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>Share securely with the user:</Text>
              <Box mt={3} p={3} bg="gray.50" borderWidth="1px" borderRadius="md">
                {tempPassword}
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setTempPassword(null)}>Done</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
}
